import { showApiErrors } from '@shared/utils'
import { FormInstance } from 'antd/lib/form'
import { AxiosError } from 'axios'

const DUPLICATE_CODES = new Set(['contact_duplicate', 'lead_duplicate'])

type DuplicateErrorData = {
  code?: string
  detail?: string
  duplicates?: Array<{
    lead_id?: string | number | null
    phone_numbers?: Array<string | number>
  }>
}

export type DuplicateData = {
  detail?: string
  items: Array<{ id: string | null; phoneNumbers: string[] }>
}

export const getErrorData = (error: unknown) =>
  ((error as AxiosError)?.response?.data ?? {}) as DuplicateErrorData

export const isDuplicateError = (error: unknown) => {
  const { code } = getErrorData(error)

  return !!code && DUPLICATE_CODES.has(code)
}

/**
 * Parse a duplicate (contact/lead) error response into the shape
 * consumed by the shared `DuplicateAlert` component.
 * Returns `null` when the error is not a duplicate.
 */
export const getDuplicateData = (error: unknown): DuplicateData | null => {
  const data = getErrorData(error)

  if (!isDuplicateError(error) || !data.duplicates?.length) {
    return null
  }

  return {
    detail: data.detail,
    items: data.duplicates.map((duplicate) => ({
      id: duplicate.lead_id != null ? String(duplicate.lead_id) : null,
      phoneNumbers: (duplicate.phone_numbers ?? []).map(String)
    }))
  }
}

/**
 * Shared mutation error handler for contact/lead forms.
 * Duplicate responses are rendered inline by `<DuplicateAlert />`,
 * so here we only surface the generic field/notification errors.
 */
export const handleContactMutationError = (
  error: unknown,
  form?: FormInstance
) => {
  if (isDuplicateError(error)) {
    return
  }

  showApiErrors(error, form)
}
